import React, { useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PageContainer from '../../../components/layout/PageContainer';
import PageHeader from '../../../components/layout/PageHeader';
import ContentCard from '../../../components/layout/ContentCard';
import EmptyState from '../../../components/layout/EmptyState';
import Table from '../../../components/data/Table';
import Pagination from '../../../components/data/Pagination';
import SearchFilter from '../../../components/data/SearchFilter';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import { translate, verifyHideCountryItems, getNameStatusClients } from '../../../utils/Common';
import { getClientConfRoleInfo, getClientTypeInfo } from '../../../utils/modules/clients';
import useClients from './useClients';
import ClientActions from './ClientActions';
import BtnSync from '../../../components/BtnSync';
import * as FaIcons from 'react-icons/fa';
import { IoMdPersonAdd } from 'react-icons/io';
import { IoPersonCircle } from 'react-icons/io5';
import { MdOutlineMailOutline, MdPhone } from 'react-icons/md';
import { GiPlatform } from 'react-icons/gi';
import { BiPackage } from 'react-icons/bi';
import { FaRegBuilding } from 'react-icons/fa';
import { verifyAccessViewPermissions } from '../../../config/permissions';

const Clients = () => {
  const history = useHistory();
  const location = useLocation();
  const permissionsPath = "/dashboard/clients";
  const canRead = verifyAccessViewPermissions(["read"], { path: permissionsPath || location.pathname });
  const canUpdate = verifyAccessViewPermissions(["update"], { path: permissionsPath || location.pathname });
  const canCreate = verifyAccessViewPermissions(["create"], { path: permissionsPath || location.pathname });
  const canSearch = verifyAccessViewPermissions(["search"], { path: permissionsPath || location.pathname });
  const canViewDetails = verifyAccessViewPermissions(["view-details"], { path: permissionsPath || location.pathname });
  // Sincronizar por cliente: la visibilidad la decide la acción `sync` asignada al rol en el
  // catálogo de permisos del módulo /dashboard/clients (el endpoint solo existe en el api de USA).
  const canSync = verifyAccessViewPermissions(["sync"], { path: permissionsPath || location.pathname });

  const [selectedField, setSelectedField] = useState('name');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedClientType, setSelectedClientType] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const {
    clients,
    loading,
    error,
    pagination,
    filters,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
  } = useClients();

  const getStatusVariant = (idEstatus) => {
    switch (idEstatus) {
      case 1:
        return 'success';
      case 2:
        return 'error';
      case 3:
        return 'warning';
      default:
        return 'neutral';
    }
  };

  // Estadísticas por estatus (activo/inactivo/pendiente) desde el servidor; así el total concuerda con activos + inactivos + pendientes
  const stats = pagination?.stats ?? { active: 0, inactive: 0, pending: 0 };

  // Definir opciones para el selector de campo en modo unificado
  const fieldOptions = [
    {
      value: 'name',
      label: translate('dashboard.clients.filters.namePlaceholder'),
    },
    {
      value: 'lastName',
      label: translate('dashboard.clients.filters.lastNamePlaceholder'),
    },
    {
      value: 'email',
      label: translate('dashboard.clients.filters.emailPlaceholder'),
    },
    {
      value: 'clientId',
      label: translate('dashboard.clients.filters.clientIdPlaceholder'),
    },
    {
      value: 'userName',
      label: translate('dashboard.clients.filters.userNamePlaceholder'),
    },
    {
      value: 'phoneNumber',
      label: translate('dashboard.clients.filters.phoneNumberPlaceholder'),
    },
  ];

  // Agregar externalId si el país no es US
  if (!verifyHideCountryItems(['us'])) {
    fieldOptions.push({
      value: 'externalId',
      label: translate('dashboard.clients.filters.externalIdPlaceholder'),
    });
  }

  // Opciones para el selector de status (con iconos)
  const statusOptions = [
    {
      value: 'all',
      label: translate('dashboard.clients.filters.allStatus') || 'Todos los estados',
      icon: <FaIcons.FaList size={16} />,
      showLabel: true,
    },
    {
      value: '1',
      label: translate('dashboard.clients.status.active'),
      icon: <FaIcons.FaCheckCircle size={16} />,
      showLabel: true,
    },
    {
      value: '2',
      label: translate('dashboard.clients.status.inactive'),
      icon: <FaIcons.FaTimesCircle size={16} />,
      showLabel: true,
    },
  ];

  // Opciones para el selector de tipo de cliente (solo iconos en el botón, pero con texto en el dropdown)
  const clientTypeOptions = [
    {
      value: 'all',
      label: translate('dashboard.clients.filters.allClientTypes'),
      icon: <FaIcons.FaUsers size={20} />,
      showLabel: true, // No mostrar label en el botón, solo el icono
    },
    {
      value: '1',
      label: translate('global.package', 'capital') || 'Package',
      icon: <BiPackage size={20} />,
      showLabel: true,
    },
    {
      value: '7',
      label: translate('global.BLI') || 'BLI',
      icon: <FaRegBuilding size={20} />,
      showLabel: true,
    },
  ];

  // Opciones para el selector de rol (IdConfRoles)
  const roleOptions = [
    {
      value: 'all',
      label: translate('dashboard.companies.detail.clients.filters.role') || 'Todos los roles',
      icon: <FaIcons.FaUserFriends size={18} />,
      showLabel: true,
    },
    {
      value: '1',
      label: translate('dashboard.companies.detail.clients.roles.customer'),
      icon: getClientConfRoleInfo(1, 18).icon,
      showLabel: true,
    },
    {
      value: '2',
      label: translate('dashboard.companies.detail.clients.roles.deliveryAgent'),
      icon: getClientConfRoleInfo(2, 18).icon,
      showLabel: true,
    },
    {
      value: '3',
      label: translate('dashboard.companies.detail.clients.roles.admin'),
      icon: getClientConfRoleInfo(3, 18).icon,
      showLabel: true,
    },
  ];

  // Sincronizar selectedStatus con el filtro actual
  React.useEffect(() => {
    if (filters.status) {
      setSelectedStatus(filters.status);
    } else {
      setSelectedStatus('all');
    }
  }, [filters.status]);

  // Sincronizar selectedClientType con el filtro actual
  React.useEffect(() => {
    if (filters.clientType) {
      setSelectedClientType(filters.clientType);
    } else {
      setSelectedClientType('all');
    }
  }, [filters.clientType]);

  // Sincronizar selectedRole con el filtro actual
  React.useEffect(() => {
    if (filters.role) {
      setSelectedRole(filters.role);
    } else {
      setSelectedRole('all');
    }
  }, [filters.role]);

  // Handler para cambios de clientType
  const handleClientTypeChange = useCallback((clientType) => {
    setSelectedClientType(clientType);
    handleFilter({
      ...filters,
      clientType: clientType !== 'all' ? clientType : undefined,
    });
  }, [filters, handleFilter]);

  // Handler para cambios de role (IdConfRoles)
  const handleRoleChange = useCallback((role) => {
    setSelectedRole(role);
    handleFilter({
      ...filters,
      role: role !== 'all' ? role : undefined,
    });
  }, [filters, handleFilter]);

  // Definir columnas de la tabla con mejoras visuales
  const columns = [
    {
      key: 'fullName',
      label: translate('dashboard.clients.columns.fullName'),
      sortable: true,
      render: (row) => {
        const fullName = `${row.Nombre || ''} ${row.Apellidos || ''}`.trim();
        const clientId = row.IdCliente ? (row.IdTipoCliente === 1 ? 'B' : '') + row.IdCliente : '';
        const displayName = fullName || '-';
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <IoPersonCircle 
              size={20} 
              style={{ 
                color: 'var(--text-tertiary, #9ca3af)', 
                flexShrink: 0 
              }} 
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div 
                style={{ 
                  fontWeight: 500, 
                  color: 'var(--text-primary, #111827)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={fullName}
              >
                {displayName}
              </div>
              {clientId && (
                <div style={{ 
                  fontSize: '12px', 
                  color: 'var(--text-tertiary, #9ca3af)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  ID: {clientId}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'email',
      label: translate('dashboard.clients.columns.email'),
      sortable: true,
      render: (row) => {
        if (!row.Email) return '-';
        return (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              minWidth: 0 
            }}
          >
            <MdOutlineMailOutline 
              size={16} 
              style={{ 
                color: 'var(--text-tertiary, #9ca3af)', 
                flexShrink: 0 
              }} 
            />
            <a
              href={`mailto:${row.Email}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                color: 'var(--color-primary, #4c5bff)',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
              title={translate('dashboard.clients.tooltips.email') + ': ' + row.Email}
            >
              {row.Email}
            </a>
          </div>
        );
      },
    },
    {
      key: 'platforms',
      label: translate('dashboard.clients.columns.platforms'),
      render: (row) => {
        const platforms = [];
        if (row.NombrePlataforma) platforms.push(row.NombrePlataforma);
        if (row.NombrePlataformaSec) platforms.push(row.NombrePlataformaSec);
        
        if (platforms.length === 0) return '-';
        
        const visiblePlatforms = platforms.slice(0, 2);
        const allPlatformsText = platforms.join(', ');
        
        return (
          <div 
            style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '4px', 
              alignItems: 'center' 
            }}
            title={translate('dashboard.clients.tooltips.platforms') + ': ' + allPlatformsText}
          >
            {visiblePlatforms.map((platform, idx) => (
              <Badge 
                key={idx} 
                variant="neutral" 
                size="sm" 
                icon={<GiPlatform size={12} />}
              >
                {platform}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'clientType',
      label: translate('dashboard.clients.columns.clientType'),
      width: '200px',
      render: (row) => {
        const clientTypeId = row?.IdTipoCliente;
        if (!clientTypeId) return '-';
        
        const { label, icon, variant } = getClientTypeInfo(clientTypeId);
        
        if (!label || label === '-') return '-';
        
        return (
          <div style={{ 
            minWidth: '180px',
            whiteSpace: 'nowrap',
            overflow: 'visible'
          }}>
            <Badge variant={variant} size="sm" icon={icon}>
              {label}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'idconfroles',
      label: translate('dashboard.companies.detail.clients.columns.role'),
      width: '150px',
      render: (row) => {
        // Priorizar IdConfRoles sobre otros campos
        const roleId = row?.IdConfRoles;
        if (!roleId) return '-';
        
        const { label, icon } = getClientConfRoleInfo(roleId);
        
        return (
          <Badge variant="neutral" icon={icon} showIcon={true} size="sm">
            {label}
          </Badge>
        );
      },
    },
    {
      key: 'status',
      label: translate('dashboard.clients.columns.status'),
      render: (row) => {
        const statusText = getNameStatusClients(row.IdEstatus) || '-';
        const capitalizedStatus = statusText.charAt(0).toUpperCase() + statusText.slice(1);
        return (
          <Badge variant={getStatusVariant(row.IdEstatus)}>
            {capitalizedStatus}
          </Badge>
        );
      },
    },
  ];

  // Acciones por fila: la columna solo se agrega cuando el rol tiene la acción `sync`.
  // El botón se limita a los clientes de tipo paquete (IdTipoCliente 1): son los únicos que el
  // api considera sincronizables (getByIdForSync), el resto responderían 404.
  if (canSync) {
    columns.push({
      key: 'actions',
      label: '',
      sortable: false,
      width: '80px',
      render: (row) => {
        if (row?.IdTipoCliente !== 1 || !row?.IdCliente) return null;

        return (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span title={translate('dashboard.clients.tooltips.sync')}>
              <BtnSync
                id={`btn-sync-client-${row.IdCliente}`}
                mode="client"
                clientId={row.IdCliente}
                size="sm"
                variant="btn"
              />
            </span>
          </div>
        );
      },
    });
  }

  const handleRowClick = (row) => {
    if (!canViewDetails) return;
    if (row?.IdCliente) {
      history.push(`/dashboard/clients/details/${row.IdCliente}`);
    }
  };

  const handleCreateClick = () => {
    if (!canCreate) return;
    history.push('/dashboard/clients/create');
  };

  if (!canRead) {
    return (
      <PageContainer>
        <PageHeader
          title={translate('global.clients')}
          icon={<FaIcons.FaUserFriends size={28} />}
          divider={false}
        />
        <ContentCard padding="md">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(107,114,128,0.12)",
                color: "#374151",
                flex: "0 0 auto",
              }}
              aria-hidden
            >
              <FaIcons.FaLock size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {translate("dashboard.configuration.noPermissionTitle")}
              </div>
              <div style={{ marginTop: 4, color: "#6B7280" }}>
                {translate("dashboard.clients.formSearch.NOT_ACCESS")}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" type="button" onClick={() => history.push("/dashboard")}>
                  {translate("global.goBack")}
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={translate('global.clients')}
        subtitle={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span>{pagination.totalItems.toLocaleString('en-US')} {translate('dashboard.clients.stats.total')}</span>
            {stats.active > 0 && (
              <Badge variant="success" size="sm">
                {stats.active} {translate('dashboard.clients.stats.active')}
              </Badge>
            )}
            {stats.inactive > 0 && (
              <Badge variant="error" size="sm">
                {stats.inactive} {translate('dashboard.clients.stats.inactive')}
              </Badge>
            )}
          </div>
        }
        icon={<FaIcons.FaUserFriends size={28} />}
        actions={
          canCreate && verifyHideCountryItems(['us']) && (
            <Button
              variant="primary"
              onClick={handleCreateClick}
              leftIcon={<IoMdPersonAdd />}
            >
              {translate('dashboard.clients.actions.create')}
            </Button>
          )
        }
      />
      {canSearch && (
        <SearchFilter
          placeholder={`${translate('global.search')} ...`}
          onSearch={handleSearch}
          onFilter={handleFilter}
          activeFilters={filters}
          loading={loading}
          unifiedMode={true}
          selectedField={selectedField}
          onFieldChange={setSelectedField}
          fieldOptions={fieldOptions}
          statusFilter={true}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          statusOptions={statusOptions}
          roleFilter={true}
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
          roleOptions={roleOptions}
          clientTypeFilter={true}
          selectedClientType={selectedClientType}
          onClientTypeChange={handleClientTypeChange}
          clientTypeOptions={clientTypeOptions}
        />
      )}

      {error && (
        <ContentCard>
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <strong>{translate('global.error')}:</strong> {error}
          </div>
        </ContentCard>
      )}

      {!loading && clients.length === 0 ? (
        <EmptyState
          icon={<FaIcons.FaUserFriends size={64} />}
          title={translate('dashboard.clients.empty.title')}
          description={translate('dashboard.clients.empty.description')}
          action={
            canCreate && verifyHideCountryItems(['us']) && (
              <Button
                variant="primary"
                onClick={handleCreateClick}
                leftIcon={<IoMdPersonAdd />}
              >
                {translate('dashboard.clients.empty.action')}
              </Button>
            )
          }
        />
      ) : (
        <Table
          columns={columns}
          data={clients}
          loading={loading}
          onSort={handleSort}
          onRowClick={handleRowClick}
          emptyMessage={translate('dashboard.clients.empty')}
          hoverable
        />
      )}

      {!loading && clients.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}
    </PageContainer>
  );
};

export default Clients;